import { supabase } from '../lib/supabase'
import { marketingParametersService } from './marketingParameters'
import { MARKETING_PARAM_DEPARTAMENTO } from '../types/marketingParameters'
import type {
  MarketingRequest,
  MarketingRequestAttachment,
  MarketingRequestComment,
  MarketingRequestFilters,
  MarketingRequestHistory,
  MarketingRequestInput,
  MarketingRequestItemInput,
  MarketingRequestStockIssueInput,
  MarketingRequestUpdateInput,
  MarketingRequestProfileRef,
  MarketingRequestStage,
  MarketingRequestStageInput,
  SoftDeleteParams,
} from '../types/marketingSolicitacao'

const ATTACHMENT_BUCKET = 'marketing-requests'
export const MARKETING_REQUEST_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024
export const MARKETING_REQUEST_ATTACHMENT_MAX_MB = 10
export const MARKETING_REQUEST_ATTACHMENT_ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,application/pdf,.pdf'

const TITLE_MAX_LENGTH = 200

function normalizeTitle(value: string | null | undefined): string {
  const trimmed = value?.trim() || ''
  if (!trimmed) {
    throw new Error('Informe o título da solicitação.')
  }
  if (trimmed.length > TITLE_MAX_LENGTH) {
    throw new Error(`O título deve ter no máximo ${TITLE_MAX_LENGTH} caracteres.`)
  }
  return trimmed
}

function normalizeNeededOn(value: string | null | undefined): string | null {
  const trimmed = value?.trim() || ''
  if (!trimmed) return null
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    throw new Error('Informe uma data válida para quando a solicitação se destina.')
  }
  const [year, month, day] = trimmed.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    throw new Error('Informe uma data válida para quando a solicitação se destina.')
  }
  return trimmed
}

function softDeletePayload(params?: SoftDeleteParams) {
  return {
    deleted_at: new Date().toISOString(),
    deleted_by: params?.deleted_by ?? null,
    deleted_reason: params?.deleted_reason ?? null,
  }
}

const REQUEST_SELECT = `
  *,
  stage:marketing_request_stages(*),
  requester:profiles!marketing_requests_requester_id_fkey(id, name, department_id, department:departments(id, name, display_name)),
  handler:profiles!marketing_requests_handler_id_fkey(id, name),
  items:marketing_request_items(*),
  attachments:marketing_request_attachments(*)
`

function mapRequest(row: MarketingRequest): MarketingRequest {
  const items = (row.items || [])
    .filter((item) => !item.deleted_at)
    .sort((a, b) => a.sort_order - b.sort_order)
  const attachments = (row.attachments || [])
    .filter((att) => !att.deleted_at)
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
  const priority =
    row.priority === 'low' || row.priority === 'medium' || row.priority === 'high'
      ? row.priority
      : 'medium'
  return { ...row, priority, items, attachments }
}

function isAllowedAttachment(file: File): boolean {
  const mime = (file.type || '').toLowerCase()
  if (mime.startsWith('image/jpeg') || mime.startsWith('image/png') || mime.startsWith('image/gif') || mime.startsWith('image/webp')) {
    return true
  }
  if (mime === 'application/pdf') return true
  return /\.(jpe?g|png|gif|webp|pdf)$/i.test(file.name)
}

export const marketingSolicitacaoService = {
  // ============================================
  // STAGES
  // ============================================
  async listStages(): Promise<MarketingRequestStage[]> {
    const { data, error } = await supabase
      .from('marketing_request_stages')
      .select('*')
      .is('deleted_at', null)
      .order('order_index', { ascending: true })

    if (error) throw error
    return (data || []) as MarketingRequestStage[]
  },

  async getInitialStageId(): Promise<string | null> {
    const { data, error } = await supabase
      .from('marketing_request_stages')
      .select('id')
      .is('deleted_at', null)
      .order('order_index', { ascending: true })
      .limit(1)
      .maybeSingle()

    if (error) throw error
    return data?.id ?? null
  },

  async createStage(input: MarketingRequestStageInput): Promise<MarketingRequestStage> {
    const { data, error } = await supabase
      .from('marketing_request_stages')
      .insert({
        name: input.name.trim(),
        key: input.key?.trim() || input.name.trim(),
        order_index: input.order_index ?? 0,
        color: input.color?.trim() || null,
        is_closed: input.is_closed ?? false,
      })
      .select('*')
      .single()

    if (error) throw error
    return data as MarketingRequestStage
  },

  async updateStage(id: string, input: Partial<MarketingRequestStageInput>): Promise<MarketingRequestStage> {
    const payload: Record<string, unknown> = {}
    if (input.name !== undefined) payload.name = input.name.trim()
    if (input.order_index !== undefined) payload.order_index = input.order_index
    if (input.color !== undefined) payload.color = input.color?.trim() || null
    if (input.is_closed !== undefined) payload.is_closed = input.is_closed

    const { data, error } = await supabase
      .from('marketing_request_stages')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
      .select('*')
      .single()

    if (error) throw error
    return data as MarketingRequestStage
  },

  async softDeleteStage(id: string, params?: SoftDeleteParams): Promise<void> {
    const { error } = await supabase
      .from('marketing_request_stages')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  // ============================================
  // REQUESTS
  // ============================================
  async listRequests(filters?: MarketingRequestFilters): Promise<MarketingRequest[]> {
    let query = supabase
      .from('marketing_requests')
      .select(REQUEST_SELECT)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })

    if (filters?.stageIds?.length) {
      query = query.in('stage_id', filters.stageIds)
    }
    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom)
    }
    if (filters?.dateTo) {
      query = query.lte('created_at', filters.dateTo)
    }

    const { data, error } = await query
    if (error) throw error

    return ((data || []) as MarketingRequest[]).map(mapRequest)
  },

  async getRequest(id: string): Promise<MarketingRequest | null> {
    const { data, error } = await supabase
      .from('marketing_requests')
      .select(REQUEST_SELECT)
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    if (!data) return null
    return mapRequest(data as MarketingRequest)
  },

  async getMarketingDepartmentId(): Promise<string | null> {
    const raw = await marketingParametersService.getValueByKey(MARKETING_PARAM_DEPARTAMENTO)
    const value = (raw || '').trim()
    return value || null
  },

  async listActiveProfilesForPicker(): Promise<{
    departmentId: string | null
    profiles: { id: string; name: string | null }[]
  }> {
    const departmentId = await this.getMarketingDepartmentId()
    if (!departmentId) {
      return { departmentId: null, profiles: [] }
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('id, name')
      .eq('is_active', true)
      .eq('department_id', departmentId)
      .order('name', { ascending: true })

    if (error) throw error
    return {
      departmentId,
      profiles: (data || []) as { id: string; name: string | null }[],
    }
  },

  async assertHandlerInMarketingDepartment(handlerId: string): Promise<void> {
    const departmentId = await this.getMarketingDepartmentId()
    if (!departmentId) {
      throw new Error(
        'Configure o parâmetro departamento_marketing em Marketing > Parâmetros antes de definir o responsável.'
      )
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', handlerId)
      .eq('is_active', true)
      .eq('department_id', departmentId)
      .maybeSingle()

    if (error) throw error
    if (!data) {
      throw new Error(
        'O responsável pela tratativa deve pertencer ao departamento configurado em departamento_marketing.'
      )
    }
  },

  async createRequest(
    input: MarketingRequestInput,
    requesterId: string
  ): Promise<MarketingRequest> {
    if (!input.items?.length) {
      throw new Error('Inclua ao menos um item na solicitação.')
    }

    const handlerId = input.handler_id?.trim() || null
    if (handlerId) {
      await this.assertHandlerInMarketingDepartment(handlerId)
    }

    const stageId = await this.getInitialStageId()
    if (!stageId) {
      throw new Error('Cadastre ao menos uma etapa em Parâmetros > Gestão antes de criar solicitações.')
    }

    const { data: request, error } = await supabase
      .from('marketing_requests')
      .insert({
        stage_id: stageId,
        requester_id: requesterId,
        handler_id: handlerId,
        title: normalizeTitle(input.title),
        notes: input.notes?.trim() || null,
        needed_on: normalizeNeededOn(input.needed_on),
        priority: 'medium',
      })
      .select('*')
      .single()

    if (error) throw error

    const itemsPayload = input.items.map((item, index) => ({
      request_id: request.id,
      sort_order: item.sort_order ?? index + 1,
      item_type: item.item_type,
      details: item.details || {},
    }))

    const { error: itemsError } = await supabase.from('marketing_request_items').insert(itemsPayload)
    if (itemsError) {
      await supabase.from('marketing_requests').delete().eq('id', request.id)
      throw itemsError
    }

    const created = await this.getRequest(request.id)
    if (!created) throw new Error('Solicitação criada, mas não foi possível recarregar.')
    return created
  },

  async updateRequest(
    id: string,
    input: MarketingRequestUpdateInput,
    params?: SoftDeleteParams
  ): Promise<MarketingRequest> {
    if (!input.items?.length) {
      throw new Error('Inclua ao menos um item na solicitação.')
    }
    if (!input.stage_id?.trim()) {
      throw new Error('Selecione a etapa da solicitação.')
    }

    const handlerId = input.handler_id?.trim() || null
    if (handlerId) {
      await this.assertHandlerInMarketingDepartment(handlerId)
    }

    const priority = input.priority
    if (priority !== 'low' && priority !== 'medium' && priority !== 'high') {
      throw new Error('Informe uma prioridade válida (Baixa, Média ou Alta).')
    }

    const payload: Record<string, unknown> = {
      stage_id: input.stage_id,
      handler_id: handlerId,
      title: normalizeTitle(input.title),
      notes: input.notes?.trim() || null,
      needed_on: normalizeNeededOn(input.needed_on),
      priority,
    }
    if (input.closed_at !== undefined) {
      payload.closed_at = input.closed_at
    }

    const { error } = await supabase
      .from('marketing_requests')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error

    await this.replaceRequestItems(id, input.items, params)

    const updated = await this.getRequest(id)
    if (!updated) throw new Error('Solicitação não encontrada após atualizar.')
    return updated
  },

  async updateRequestStage(
    id: string,
    stageId: string,
    options?: { closed_at?: string | null }
  ): Promise<MarketingRequest> {
    const payload: Record<string, unknown> = { stage_id: stageId }
    if (options && 'closed_at' in options) {
      payload.closed_at = options.closed_at ?? null
    }

    const { error } = await supabase
      .from('marketing_requests')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error

    const updated = await this.getRequest(id)
    if (!updated) throw new Error('Solicitação não encontrada após atualizar a etapa.')
    return updated
  },

  async updateRequestHandler(id: string, handlerId: string): Promise<MarketingRequest> {
    if (!handlerId?.trim()) {
      throw new Error('Selecione o responsável pela tratativa.')
    }
    await this.assertHandlerInMarketingDepartment(handlerId)

    const { error } = await supabase
      .from('marketing_requests')
      .update({ handler_id: handlerId })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error

    const updated = await this.getRequest(id)
    if (!updated) throw new Error('Solicitação não encontrada após atualizar o responsável.')
    return updated
  },

  async updateRequestNotes(id: string, notes: string | null): Promise<MarketingRequest> {
    const { error } = await supabase
      .from('marketing_requests')
      .update({ notes: notes?.trim() || null })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error

    const updated = await this.getRequest(id)
    if (!updated) throw new Error('Solicitação não encontrada após atualizar.')
    return updated
  },

  async replaceRequestItems(
    requestId: string,
    items: MarketingRequestItemInput[],
    params?: SoftDeleteParams
  ): Promise<MarketingRequest> {
    if (!items.length) {
      throw new Error('Inclua ao menos um item na solicitação.')
    }

    const { data: existing, error: existingError } = await supabase
      .from('marketing_request_items')
      .select('id')
      .eq('request_id', requestId)
      .is('deleted_at', null)

    if (existingError) throw existingError

    if ((existing || []).length > 0) {
      const { error: softError } = await supabase
        .from('marketing_request_items')
        .update(softDeletePayload(params))
        .eq('request_id', requestId)
        .is('deleted_at', null)
      if (softError) throw softError
    }

    const payload = items.map((item, index) => ({
      request_id: requestId,
      sort_order: item.sort_order ?? index + 1,
      item_type: item.item_type,
      details: item.details || {},
    }))

    const { error: insertError } = await supabase.from('marketing_request_items').insert(payload)
    if (insertError) throw insertError

    const updated = await this.getRequest(requestId)
    if (!updated) throw new Error('Solicitação não encontrada após atualizar itens.')
    return updated
  },

  async softDeleteRequest(id: string, params?: SoftDeleteParams): Promise<void> {
    const { error: itemsError } = await supabase
      .from('marketing_request_items')
      .update(softDeletePayload(params))
      .eq('request_id', id)
      .is('deleted_at', null)

    if (itemsError) throw itemsError

    const { error: attachmentsError } = await supabase
      .from('marketing_request_attachments')
      .update(softDeletePayload(params))
      .eq('request_id', id)
      .is('deleted_at', null)

    if (attachmentsError) throw attachmentsError

    const { error } = await supabase
      .from('marketing_requests')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  // ============================================
  // ATTACHMENTS
  // ============================================
  async listAttachments(requestId: string): Promise<MarketingRequestAttachment[]> {
    const { data, error } = await supabase
      .from('marketing_request_attachments')
      .select('*')
      .eq('request_id', requestId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true })

    if (error) throw error
    return (data || []) as MarketingRequestAttachment[]
  },

  async uploadAttachment(
    requestId: string,
    file: File,
    uploadedBy?: string | null
  ): Promise<MarketingRequestAttachment> {
    if (!isAllowedAttachment(file)) {
      throw new Error(`Arquivo não permitido: ${file.name}. Use imagem (JPG, PNG, GIF, WebP) ou PDF.`)
    }
    if (file.size > MARKETING_REQUEST_ATTACHMENT_MAX_BYTES) {
      throw new Error(
        `O arquivo "${file.name}" excede o limite de ${MARKETING_REQUEST_ATTACHMENT_MAX_MB} MB.`
      )
    }

    const timestamp = Date.now()
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
    const filePath = `${requestId}/${timestamp}_${sanitizedFileName}`

    const { error: uploadError } = await supabase.storage.from(ATTACHMENT_BUCKET).upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || undefined,
    })

    if (uploadError) {
      throw new Error(`Falha ao enviar anexo: ${uploadError.message}`)
    }

    const { data, error } = await supabase
      .from('marketing_request_attachments')
      .insert({
        request_id: requestId,
        file_path: filePath,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || null,
        uploaded_by: uploadedBy || null,
      })
      .select('*')
      .single()

    if (error) {
      await supabase.storage.from(ATTACHMENT_BUCKET).remove([filePath])
      throw error
    }

    return data as MarketingRequestAttachment
  },

  async getAttachmentUrl(filePath: string, expiresInSeconds = 3600): Promise<string | null> {
    if (!filePath) return null
    const { data, error } = await supabase.storage
      .from(ATTACHMENT_BUCKET)
      .createSignedUrl(filePath, expiresInSeconds)

    if (error) {
      console.error('Erro ao gerar URL assinada do anexo:', error.message)
      return null
    }
    return data?.signedUrl ?? null
  },

  async softDeleteAttachment(id: string, params?: SoftDeleteParams): Promise<void> {
    const { data: attachment, error: fetchError } = await supabase
      .from('marketing_request_attachments')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (fetchError) throw fetchError
    if (!attachment) return

    const { error } = await supabase
      .from('marketing_request_attachments')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error

    try {
      await supabase.storage.from(ATTACHMENT_BUCKET).remove([attachment.file_path])
    } catch {
      // Mantém o soft delete mesmo se o storage falhar
    }
  },

  // ============================================
  // COMMENTS
  // ============================================
  async listComments(requestId: string): Promise<MarketingRequestComment[]> {
    const { data, error } = await supabase
      .from('marketing_request_comments')
      .select('*, author:user_id(id, name)')
      .eq('request_id', requestId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true })

    if (error) throw error
    return (data || []) as MarketingRequestComment[]
  },

  async addComment(requestId: string, content: string): Promise<MarketingRequestComment> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    const trimmed = content.trim()
    if (!trimmed) throw new Error('Informe o texto do comentário.')

    const { data, error } = await supabase
      .from('marketing_request_comments')
      .insert({ request_id: requestId, user_id: user.id, content: trimmed })
      .select('*, author:user_id(id, name)')
      .single()

    if (error) throw error
    return data as MarketingRequestComment
  },

  async updateComment(id: string, content: string): Promise<MarketingRequestComment> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    const trimmed = content.trim()
    if (!trimmed) throw new Error('Informe o texto do comentário.')

    const { data, error } = await supabase
      .from('marketing_request_comments')
      .update({ content: trimmed })
      .eq('id', id)
      .eq('user_id', user.id)
      .is('deleted_at', null)
      .select('*, author:user_id(id, name)')
      .single()

    if (error) throw error
    return data as MarketingRequestComment
  },

  async deleteComment(id: string): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    const { data, error } = await supabase
      .from('marketing_request_comments')
      .update({ deleted_at: new Date().toISOString(), deleted_by: user.id })
      .eq('id', id)
      .eq('user_id', user.id)
      .is('deleted_at', null)
      .select('id')
      .maybeSingle()

    if (error) throw error
    if (!data) throw new Error('Não foi possível excluir o comentário.')
  },

  // ============================================
  // HISTORY
  // ============================================
  async listHistory(requestId: string): Promise<MarketingRequestHistory[]> {
    const { data, error } = await supabase
      .from('marketing_request_history')
      .select('*, changer:changed_by(id, name)')
      .eq('request_id', requestId)
      .order('changed_at', { ascending: false })

    if (error) throw error
    return (data || []) as MarketingRequestHistory[]
  },

  async listProfilesByIds(ids: string[]): Promise<MarketingRequestProfileRef[]> {
    const unique = Array.from(new Set(ids.filter(Boolean)))
    if (unique.length === 0) return []

    const { data, error } = await supabase
      .from('profiles')
      .select('id, name')
      .in('id', unique)

    if (error) throw error
    return (data || []) as MarketingRequestProfileRef[]
  },

  // ============================================
  // STOCK ISSUES (finalização)
  // ============================================
  async createStockIssues(
    requestId: string,
    issues: MarketingRequestStockIssueInput[],
    createdBy?: string | null
  ): Promise<void> {
    if (!issues.length) return

    const payload = issues.map((issue) => ({
      request_id: requestId,
      stock_item_id: issue.stock_item_id,
      location_id: issue.location_id,
      quantity: issue.quantity,
      movement_id: issue.movement_id ?? null,
      created_by: createdBy ?? null,
    }))

    const { error } = await supabase.from('marketing_request_stock_issues').insert(payload)
    if (error) throw error
  },
}
